import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "fyp:theme";
type Theme = "light" | "dark";

const applyThemeClass = (theme: Theme) => {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (theme === "dark") root.classList.add("dark");
  else root.classList.remove("dark");
};

const readInitial = (): Theme => {
  if (typeof window === "undefined") return "light";
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark") return stored;
  } catch {
    /* ignore */
  }
  if (typeof window.matchMedia === "function" && window.matchMedia("(prefers-color-scheme: dark)").matches) {
    return "dark";
  }
  return "light";
};

/**
 * Hook de modo oscuro/claro (Entrega 4.1).
 * - Aplica `.dark` en <html> al montar.
 * - Persiste en localStorage.
 * - Respeta `prefers-color-scheme` por defecto (sólo si no hay preferencia guardada).
 */
export const useTheme = () => {
  const [theme, setThemeState] = useState<Theme>(() => readInitial());

  // Aplica al montar y cuando cambia.
  useEffect(() => {
    applyThemeClass(theme);
  }, [theme]);

  // Si el usuario nunca eligió, sigue al sistema.
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    let stored: string | null = null;
    try { stored = window.localStorage.getItem(STORAGE_KEY); } catch { /* ignore */ }
    if (stored === "light" || stored === "dark") return;
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent) => setThemeState(e.matches ? "dark" : "light");
    media.addEventListener?.("change", handler);
    return () => media.removeEventListener?.("change", handler);
  }, []);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    try { window.localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => {
      const next: Theme = prev === "dark" ? "light" : "dark";
      try { window.localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
      return next;
    });
  }, []);

  return { theme, setTheme, toggleTheme };
};
